import { useState, useEffect } from 'react';
import { productReviews, ProductReview } from '../../../mocks/productReviews';
import ReviewCard from './ReviewCard';
import ReviewForm from './ReviewForm';

interface ProductReviewsProps {
  beverageId: string;
  beverageName: string;
  baseRating: number;
  baseCount: number;
}

type SortOption = 'recent' | 'helpful' | 'highest' | 'lowest';

function RatingSummary({ reviews, baseRating, baseCount }: { reviews: ProductReview[]; baseRating: number; baseCount: number }) {
  const dist: Record<number, number> = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };

  // Use base rating proportions for mock visual accuracy
  const totalBase = baseCount;
  const mockDist: Record<number, number> = {
    5: Math.round(totalBase * (baseRating >= 4.7 ? 0.76 : baseRating >= 4.5 ? 0.65 : 0.55)),
    4: Math.round(totalBase * (baseRating >= 4.7 ? 0.16 : baseRating >= 4.5 ? 0.22 : 0.27)),
    3: Math.round(totalBase * (baseRating >= 4.7 ? 0.05 : 0.09)),
    2: Math.round(totalBase * 0.02),
    1: Math.round(totalBase * 0.01),
  };

  reviews.forEach((r) => { dist[r.rating] = (dist[r.rating] || 0) + 1; });
  const combined: Record<number, number> = {
    5: (mockDist[5] || 0) + (dist[5] || 0),
    4: (mockDist[4] || 0) + (dist[4] || 0),
    3: (mockDist[3] || 0) + (dist[3] || 0),
    2: (mockDist[2] || 0) + (dist[2] || 0),
    1: (mockDist[1] || 0) + (dist[1] || 0),
  };
  const total = Object.values(combined).reduce((a, b) => a + b, 0);

  return (
    <div className="flex items-start gap-10 bg-white rounded-2xl border border-gray-100 p-6 mb-8">
      {/* Big score */}
      <div className="flex flex-col items-center gap-2 flex-shrink-0">
        <span className="text-6xl font-black text-gray-900 leading-none">{baseRating}</span>
        <div className="flex items-center gap-0.5">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className={`w-5 h-5 flex items-center justify-center ${i < Math.floor(baseRating) ? 'text-amber-400' : i < baseRating ? 'text-amber-300' : 'text-gray-200'}`}>
              <i className="ri-star-fill text-base" />
            </div>
          ))}
        </div>
        <p className="text-xs text-gray-400">{baseCount.toLocaleString()} reviews</p>
      </div>

      {/* Bar distribution */}
      <div className="flex-1 space-y-2">
        {[5, 4, 3, 2, 1].map((star) => {
          const count = combined[star] || 0;
          const pct = total > 0 ? Math.round((count / total) * 100) : 0;
          return (
            <div key={star} className="flex items-center gap-3">
              <div className="flex items-center gap-0.5 w-16 flex-shrink-0">
                <span className="text-xs font-medium text-gray-700">{star}</span>
                <div className="w-4 h-4 flex items-center justify-center text-amber-400">
                  <i className="ri-star-fill text-xs" />
                </div>
              </div>
              <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-700 ${star >= 4 ? 'bg-amber-400' : star === 3 ? 'bg-yellow-300' : 'bg-gray-300'}`}
                  style={{ width: `${pct}%` }}
                />
              </div>
              <span className="text-xs text-gray-400 w-8 text-right flex-shrink-0">{pct}%</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function ProductReviews({ beverageId, beverageName, baseRating, baseCount }: ProductReviewsProps) {
  const [userReviews, setUserReviews] = useState<ProductReview[]>([]);
  const [filterRating, setFilterRating] = useState<number>(0);
  const [sort, setSort] = useState<SortOption>('recent');
  const [showForm, setShowForm] = useState(false);
  const [pendingBanner, setPendingBanner] = useState(false);

  useEffect(() => {
    const key = `sipdash_reviews_${beverageId}`;
    try {
      const stored: ProductReview[] = JSON.parse(localStorage.getItem(key) || '[]');
      setUserReviews(stored);
    } catch {
      setUserReviews([]);
    }
  }, [beverageId]);

  const mockForProduct = productReviews.filter((r) => r.beverageId === beverageId);
  const allReviews = [...userReviews, ...mockForProduct];

  const filtered = allReviews.filter((r) => filterRating === 0 || r.rating === filterRating);

  const sorted = [...filtered].sort((a, b) => {
    if (sort === 'helpful') return b.helpful - a.helpful;
    if (sort === 'highest') return b.rating - a.rating;
    if (sort === 'lowest') return a.rating - b.rating;
    // recent: user-submitted first, then by id (newer mock = higher pr number)
    const aIsUser = a.id.startsWith('user_');
    const bIsUser = b.id.startsWith('user_');
    if (aIsUser && !bIsUser) return -1;
    if (!aIsUser && bIsUser) return 1;
    return b.id.localeCompare(a.id);
  });

  const handleNewReview = () => {
    // Review goes to moderation — show pending banner instead of adding to list
    setShowForm(false);
    setPendingBanner(true);
  };

  const ratingCounts: Record<number, number> = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
  allReviews.forEach((r) => { ratingCounts[r.rating] = (ratingCounts[r.rating] || 0) + 1; });

  return (
    <section className="mt-16 pt-12 border-t border-gray-100">
      {/* Header */}
      <div className="flex items-end justify-between mb-8">
        <div>
          <p className="text-xs font-bold text-[#FF6B35] uppercase tracking-widest mb-1">Customer Feedback</p>
          <h3
            className="text-3xl font-black text-gray-900"
            style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.02em' }}
          >
            Reviews <span className="text-[#FF6B35]">/</span> Ratings
          </h3>
        </div>
        {!showForm && (
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center gap-2 bg-[#FF6B35] hover:bg-[#e85d2a] text-white font-semibold px-5 py-2.5 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-edit-line text-sm" />
            </div>
            Write a Review
          </button>
        )}
      </div>

      {/* Rating Summary */}
      <RatingSummary reviews={allReviews} baseRating={baseRating} baseCount={baseCount} />

      {/* Write Review Form */}
      {showForm && (
        <div className="mb-8">
          <ReviewForm
            beverageId={beverageId}
            beverageName={beverageName}
            onSubmit={handleNewReview}
            onCancel={() => setShowForm(false)}
          />
        </div>
      )}

      {/* Pending moderation banner */}
      {pendingBanner && !showForm && (
        <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-2xl px-5 py-4 mb-6">
          <div className="w-8 h-8 flex items-center justify-center rounded-full bg-amber-100 text-amber-500 flex-shrink-0 mt-0.5">
            <i className="ri-time-line text-base" />
          </div>
          <div>
            <p className="text-sm font-bold text-amber-900">Review awaiting moderation</p>
            <p className="text-xs text-amber-700 mt-0.5 leading-relaxed">
              Your review has been submitted and will appear here once approved by our team.
            </p>
          </div>
          <button
            onClick={() => setPendingBanner(false)}
            className="w-5 h-5 flex items-center justify-center text-amber-400 hover:text-amber-600 cursor-pointer flex-shrink-0 mt-0.5"
          >
            <i className="ri-close-line text-sm" />
          </button>
        </div>
      )}

      {/* Filters + Sort bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        {/* Star filter pills */}
        <div className="flex items-center gap-2 flex-wrap">
          {([0, 5, 4, 3, 2, 1] as const).map((star) => (
            <button
              key={star}
              onClick={() => setFilterRating(star)}
              className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold transition-all cursor-pointer whitespace-nowrap border ${
                filterRating === star
                  ? 'bg-[#FF6B35] text-white border-[#FF6B35]'
                  : 'bg-white text-gray-600 border-gray-200 hover:border-[#FF6B35] hover:text-[#FF6B35]'
              }`}
            >
              {star === 0 ? (
                'All reviews'
              ) : (
                <>
                  {star}
                  <div className="w-3 h-3 flex items-center justify-center">
                    <i className="ri-star-fill text-xs" />
                  </div>
                  {ratingCounts[star] > 0 && <span className="opacity-70">({ratingCounts[star]})</span>}
                </>
              )}
            </button>
          ))}
        </div>

        {/* Sort */}
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400 whitespace-nowrap">Sort by:</span>
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value as SortOption)}
            className="text-xs bg-white border border-gray-200 rounded-xl px-3 py-2 text-gray-700 outline-none focus:border-[#FF6B35] cursor-pointer"
          >
            <option value="recent">Most Recent</option>
            <option value="helpful">Most Helpful</option>
            <option value="highest">Highest Rating</option>
            <option value="lowest">Lowest Rating</option>
          </select>
        </div>
      </div>

      {/* Review count */}
      <p className="text-sm text-gray-500 mb-5">
        Showing <strong className="text-gray-800">{sorted.length}</strong> review{sorted.length !== 1 ? 's' : ''}
        {filterRating > 0 && <> for <span className="text-amber-500 font-semibold">{filterRating}★</span></>}
      </p>

      {/* Review list */}
      {sorted.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-2xl border border-gray-100">
          <div className="w-14 h-14 flex items-center justify-center rounded-full bg-gray-100 text-gray-400 mx-auto mb-4">
            <i className="ri-chat-3-line text-2xl" />
          </div>
          <p className="text-gray-600 font-medium">No reviews yet for this filter</p>
          <p className="text-sm text-gray-400 mt-1">
            {filterRating > 0 ? 'Try a different star rating.' : 'Be the first to review this product!'}
          </p>
          {filterRating === 0 && !showForm && (
            <button
              onClick={() => setShowForm(true)}
              className="mt-4 text-[#FF6B35] text-sm font-semibold hover:underline cursor-pointer"
            >
              Write the first review
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {sorted.map((review) => (
            <ReviewCard
              key={review.id}
              review={review}
              isNew={review.id.startsWith('user_')}
            />
          ))}
        </div>
      )}
    </section>
  );
}
